// MinhasAvaliacoes.js — Aba "Avaliações" do perfil, em grade estilo Letterboxd.
//
// ================= DUAS SUB-ABAS (pedido do dono, 11/09/2026) =================
//
//   Avaliações ...... o que a pessoa já escreveu, com filtro por nota e ordem
//   Não avaliados ... os livros da estante que ainda esperam nota
//
// A segunda existia só como frase no estado vazio ("vá para Meus E-books"), e
// era ela que respondia a pergunta que traz a pessoa a esta aba: "o que falta
// eu avaliar?" — que é também onde estão os Insigne Coins que ela ainda não
// recebeu. As duas têm busca e paginação; só a primeira tem filtro de nota,
// porque na segunda não existe nota para filtrar.
//
// O CARD: capa + título + estrelas. O Letterboxd mostra só a capa; aqui o
// título entra porque o acervo é acadêmico e várias capas se parecem muito
// (as três "Direito Aplicado" são quase idênticas) — só a capa não identifica
// o livro para quem está procurando o que escreveu.
//
// A altura do título é FIXA em duas linhas, não `min-h`: com altura mínima, um
// título de uma linha e outro de duas empurrariam as estrelas para alturas
// diferentes e a grade ficaria serrilhada.
//
// ================= `opacity-0` É UMA ARMADILHA NESTE PROJETO =================
//
// O site carrega Bootstrap junto do Tailwind, e o Bootstrap declara
// `.opacity-0{opacity:0!important}`. O `!important` vence qualquer variante do
// Tailwind (que não usa `!important`), então `opacity-0 group-hover:opacity-100`
// deixa o elemento invisível PARA SEMPRE — era o que acontecia com as legendas
// "Editar" e "Avaliar" das capas. `opacity-[0]` gera outra classe, que o
// Bootstrap não conhece, e o hover volta a funcionar.
//
// Clicar num card de avaliação abre o `ModalAvaliacao`. Clicar num card de "não
// avaliado" leva à seção de opiniões do livro, que é onde a avaliação NASCE —
// o modal daqui só sabe editar o que já existe.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useMinhasAvaliacoes from "../../../hooks/useMinhasAvaliacoes";
import useEstante from "../../../hooks/useEstante";
import ModalAvaliacao from "./ModalAvaliacao";
import Stars from "../../produto/paginaProduto/Stars";
import RevealStagger from "../../ui/RevealStagger";
import Pagination from "../../utils/Pagination";
import useBarraGrudada, { CLASSES_ESCONDIDA, CLASSES_SUMICO } from "../../../hooks/useBarraGrudada";
import { SectionCard } from "../ui/ProfileUI";
import { COINS_POR_AVALIACAO } from "../../../config/coins";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

// Dez por página: dois "andares" da grade de cinco colunas no desktop, e
// múltiplo das larguras menores (2 e 3 por linha) — nenhuma página termina com
// uma fileira pela metade.
const POR_PAGINA = 10;

const ORDENS = [
  { chave: "recentes", rotulo: "Mais recentes" },
  { chave: "maior", rotulo: "Maior nota" },
  { chave: "menor", rotulo: "Menor nota" },
];

const semAcento = (s) =>
  String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const quando = (a) => new Date(a?.updated_at || a?.created_at || 0).getTime() || 0;

/** A moldura da capa, igual nas duas sub-abas. */
const Capa = ({ livro, children }) => (
  <div
    className="relative overflow-hidden rounded-lg border-2 border-insigne-sombra
               bg-insigne-navy-deep aspect-[3/4]
               shadow-[3px_3px_0_0_rgb(var(--c-sombra))]
               transition-[box-shadow,transform] duration-200
               group-hover/aval:shadow-[6px_6px_0_0_rgb(var(--c-sombra))]
               group-hover/aval:-translate-x-[3px] group-hover/aval:-translate-y-[3px]"
  >
    {livro?.image ? (
      <img
        src={`${API_URL}public/${livro.image}`}
        alt={livro.titulo}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 ease-out
                   group-hover/aval:scale-[1.05]"
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center">
        <i className="bi bi-book text-white/30 text-2xl" />
      </div>
    )}
    {children}
  </div>
);

const Titulo = ({ children }) => (
  <h4 className="mt-2 text-sm font-bold text-white leading-snug h-10 line-clamp-2 overflow-hidden">
    {children}
  </h4>
);

const MinhasAvaliacoes = () => {
  const { avaliacoes, total, loading, erro, salvando, salvar } = useMinhasAvaliacoes();
  // A estante é a lista do que PODE ser avaliado — o mesmo recorte da aba
  // E-books, e por isso vem do mesmo hook em vez de uma regra nova aqui.
  const { livros, loading: carregandoEstante } = useEstante();

  const [aba, setAba] = useState("feitas");
  const [busca, setBusca] = useState("");
  const [nota, setNota] = useState(0); // 0 = todas
  const [ordem, setOrdem] = useState("recentes");
  const [pagina, setPagina] = useState(1);
  const [abertaId, setAbertaId] = useState(null);

  const aberta = avaliacoes.find((a) => a.id === abertaId) || null;

  // Trocar de sub-aba, buscar ou filtrar invalida a página em que a pessoa
  // estava: filtrar da página 3 com uma página de resultado dá grade vazia.
  useEffect(() => { setPagina(1); }, [aba, busca, nota, ordem]);

  const idsAvaliados = useMemo(
    () => new Set(avaliacoes.map((a) => String(a.livro?.id))),
    [avaliacoes]
  );

  const naoAvaliados = useMemo(
    () => livros.filter((l) => !idsAvaliados.has(String(l.id))),
    [livros, idsAvaliados]
  );

  // Quantas avaliações em cada nota — é o número dentro de cada chip do filtro,
  // e é ele que permite esconder o filtro que não acharia nada.
  const porNota = useMemo(() => {
    const conta = [0, 0, 0, 0, 0, 0];
    avaliacoes.forEach((a) => {
      const n = Math.round(Number(a.nota) || 0);
      if (n >= 1 && n <= 5) conta[n] += 1;
    });
    return conta;
  }, [avaliacoes]);

  const feitasFiltradas = useMemo(() => {
    const termo = semAcento(busca).trim();
    const lista = avaliacoes.filter((a) => {
      if (nota && Math.round(Number(a.nota) || 0) !== nota) return false;
      if (!termo) return true;
      // O comentário entra na busca: quem procura aqui muitas vezes lembra do
      // que escreveu, não do título exato do livro.
      return semAcento(
        [a.livro?.titulo, a.livro?.subtitulo, a.comentario].filter(Boolean).join(" ")
      ).includes(termo);
    });

    const ordenada = [...lista];
    if (ordem === "recentes") ordenada.sort((a, b) => quando(b) - quando(a));
    // Empate de nota volta a ser resolvido pela data: sem isso, cinco livros
    // com 5 estrelas saem numa ordem que muda a cada carregamento.
    if (ordem === "maior") ordenada.sort((a, b) => (b.nota - a.nota) || (quando(b) - quando(a)));
    if (ordem === "menor") ordenada.sort((a, b) => (a.nota - b.nota) || (quando(b) - quando(a)));
    return ordenada;
  }, [avaliacoes, busca, nota, ordem]);

  const pendentesFiltrados = useMemo(() => {
    const termo = semAcento(busca).trim();
    const lista = naoAvaliados.filter((l) => {
      if (!termo) return true;
      const autores = Array.isArray(l.autores) ? l.autores.join(" ") : "";
      return semAcento([l.titulo, l.subtitulo, autores].filter(Boolean).join(" ")).includes(termo);
    });
    // Quem JÁ PODE avaliar vem antes: é a lista de coins que estão na mesa.
    return [...lista].sort(
      (a, b) => (b.pode_avaliar ? 1 : 0) - (a.pode_avaliar ? 1 : 0)
        || String(a.titulo || "").localeCompare(String(b.titulo || ""), "pt-BR")
    );
  }, [naoAvaliados, busca]);

  const itens = aba === "feitas" ? feitasFiltradas : pendentesFiltrados;
  const totalPaginas = Math.max(1, Math.ceil(itens.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicio = (paginaAtual - 1) * POR_PAGINA;
  const daPagina = itens.slice(inicio, inicio + POR_PAGINA);

  const carregando = aba === "feitas" ? loading : carregandoEstante;

  // A busca e os filtros acompanham a rolagem e somem quando o cabeçalho do
  // site volta — a regra de TODA barra de busca da loja desde 11/09/2026
  // (useBarraGrudada). Antes esta rolava junto com a lista, e quem chegava ao
  // fim de uma página de avaliações tinha de subir tudo para trocar a nota.
  const { barraRef, sentinelaRef, esconder: esconderBarra } = useBarraGrudada();

  const ABAS = [
    { chave: "feitas", rotulo: "Avaliações", n: total },
    { chave: "pendentes", rotulo: "Não avaliados", n: naoAvaliados.length },
  ];

  return (
    <>
      <SectionCard title="Avaliações">
        {/* ===== As duas sub-abas ===== */}
        <div className="-mt-2 mb-4 flex w-max max-w-full gap-1.5 overflow-x-auto rounded-xl border border-line
                        bg-insigne-navy-deep/50 p-1">
          {ABAS.map((a) => (
            <button
              key={a.chave}
              type="button"
              onClick={() => setAba(a.chave)}
              aria-pressed={aba === a.chave}
              className={
                "foco-laranja inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3.5 py-2 " +
                "text-sm font-semibold transition-colors " +
                (aba === a.chave
                  ? "bg-insigne-orange text-paper"
                  : "text-white/65 hover:bg-white/5 hover:text-white")
              }
            >
              <i className={"bi " + (a.chave === "feitas" ? "bi-star-fill" : "bi-hourglass-split")} />
              {a.rotulo}
              <span className={aba === a.chave ? "text-paper/70" : "text-white/35"}>{a.n}</span>
            </button>
          ))}
        </div>

        <p className="text-white/55 text-sm mb-4">
          {aba === "feitas"
            ? "Os livros que você avaliou. Clique em um card para editar a nota ou o texto."
            : `Livros da sua estante sem avaliação. Cada uma rende ${COINS_POR_AVALIACAO} Insigne Coins.`}
        </p>

        {/* ===== Busca + (só nas feitas) nota e ordem =====
            A barra gruda logo abaixo da barra do nome do perfil (as duas
            variáveis vêm do Perfil.js), com o fundo do CARTÃO e não o da
            página: ela mora dentro do SectionCard, e `-mx-8 px-8` estica o
            fundo até as bordas de dentro dele. */}
        <div ref={sentinelaRef} aria-hidden="true" className="h-0" />
        <div
          ref={barraRef}
          className={"mb-5 md:sticky md:z-20 md:-mx-8 md:px-8 md:py-3 md:bg-insigne-navy-soft " +
                     "md:top-[calc(var(--topo-perfil,0px)_+_var(--altura-cabecalho-perfil,0px))] " +
                     CLASSES_SUMICO + " " + (esconderBarra ? CLASSES_ESCONDIDA : "")}
        >
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[min(100%,220px)]">
            <i className="bi bi-search absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-white/40 pointer-events-none" />
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder={aba === "feitas" ? "Buscar por título ou pelo que você escreveu..." : "Buscar por título ou autor..."}
              aria-label="Buscar nas avaliações"
              className="foco-laranja w-full rounded-lg border border-line bg-white/5 py-2.5 pl-10 pr-3
                         text-sm text-white placeholder:text-white/40 outline-none
                         transition-colors focus:border-insigne-orange/50 focus:bg-white/[0.07]"
            />
          </div>

          {aba === "feitas" && (
            <div className="relative">
              <select
                value={ordem}
                onChange={(e) => setOrdem(e.target.value)}
                aria-label="Ordenar avaliações"
                className="foco-laranja appearance-none rounded-lg border border-line bg-insigne-navy-deep py-2.5 pl-4 pr-9
                           text-sm text-white/85 outline-none transition-colors hover:border-white/30"
              >
                {ORDENS.map((o) => (
                  <option key={o.chave} value={o.chave}>{o.rotulo}</option>
                ))}
              </select>
              <i className="bi bi-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/50" />
            </div>
          )}
        </div>

        {/* Filtro por nota: só onde existe nota. As estrelas ficam no chip
            porque "4" sozinho, ao lado de um "12" de contagem, vira conta. */}
        {aba === "feitas" && avaliacoes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            <button
              type="button"
              onClick={() => setNota(0)}
              aria-pressed={nota === 0}
              className={
                "foco-laranja rounded-controle border px-3 py-1 text-xs font-semibold transition-colors " +
                (nota === 0
                  ? "border-insigne-orange bg-insigne-orange text-paper"
                  : "border-line bg-white/5 text-white/70 hover:bg-white/10 hover:text-white")
              }
            >
              Todas <span className={nota === 0 ? "text-paper/70" : "text-white/35"}>{total}</span>
            </button>

            {[5, 4, 3, 2, 1].map((n) => (
              porNota[n] > 0 && (
                <button
                  key={n}
                  type="button"
                  onClick={() => setNota(nota === n ? 0 : n)}
                  aria-pressed={nota === n}
                  className={
                    "foco-laranja inline-flex items-center gap-1.5 rounded-controle border px-3 py-1 text-xs " +
                    "font-semibold transition-colors " +
                    (nota === n
                      ? "border-insigne-orange bg-insigne-orange text-paper"
                      : "border-line bg-white/5 text-white/70 hover:bg-white/10 hover:text-white")
                  }
                >
                  {n} <i className="bi bi-star-fill text-[10px]" />
                  <span className={nota === n ? "text-paper/70" : "text-white/35"}>{porNota[n]}</span>
                </button>
              )
            ))}
          </div>
        )}

        </div>

        {carregando ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="rounded-lg bg-insigne-navy-soft aspect-[3/4]" />
                <div className="h-3 w-4/5 rounded bg-insigne-navy-soft mt-2" />
                <div className="h-3 w-1/2 rounded bg-insigne-navy-soft mt-2" />
              </div>
            ))}
          </div>
        ) : erro && aba === "feitas" ? (
          <p className="text-amber-300 text-sm">{erro}</p>
        ) : itens.length === 0 ? (
          <div className="text-center py-12 text-white/55">
            <i className={"bi text-4xl mb-3 block " + (busca || nota ? "bi-search" : "bi-star")} />
            {busca || nota ? (
              <>
                <p className="font-medium text-white">Nada encontrado</p>
                <p className="text-sm mt-1">Tente outra busca ou tire o filtro de nota.</p>
                <button
                  onClick={() => { setBusca(""); setNota(0); }}
                  className="btn-solido mt-4 rounded-controle px-5 py-2 text-sm font-semibold"
                >
                  Limpar busca e filtros
                </button>
              </>
            ) : aba === "feitas" ? (
              <>
                <p className="font-medium text-white">Você ainda não avaliou nenhum livro</p>
                <p className="text-sm mt-1">
                  Cada avaliação rende {COINS_POR_AVALIACAO} Insigne Coins, e só quem comprou pode avaliar.
                </p>
                <button
                  onClick={() => setAba("pendentes")}
                  className="mt-5 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold
                             bg-insigne-orange text-paper hover:bg-insigne-orange-dark transition-colors"
                >
                  <i className="bi bi-hourglass-split" /> Ver o que dá para avaliar
                </button>
              </>
            ) : (
              <>
                <p className="font-medium text-white">Nenhum livro esperando avaliação</p>
                <p className="text-sm mt-1">Você já avaliou tudo o que está na sua estante.</p>
                <Link
                  to={main + "livros"}
                  className="mt-5 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold
                             bg-insigne-orange text-paper hover:bg-insigne-orange-dark transition-colors"
                >
                  <i className="bi bi-search" /> Ver o catálogo
                </Link>
              </>
            )}
          </div>
        ) : (
          <>
            <p className="text-xs text-white/45 mb-3">
              Mostrando {inicio + 1}–{Math.min(inicio + POR_PAGINA, itens.length)} de {itens.length}
            </p>

            <div id="avaliacoes-topo" className="h-0 scroll-mt-24 md:scroll-mt-[280px]" aria-hidden="true" />

            <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5 orfa-ao-centro">
              {daPagina.map((item) =>
                aba === "feitas" ? (
                  <RevealStagger.Item key={item.id}>
                    <button
                      onClick={() => setAbertaId(item.id)}
                      className="group/aval block w-full text-left"
                      aria-label={`Editar sua avaliação de ${item.livro.titulo}`}
                    >
                      <Capa livro={item.livro}>
                        {/* Despublicada pela moderação: a pessoa precisa saber
                            que o texto dela não está mais visível no site —
                            descobrir isso sozinha, visitando a página do livro,
                            seria pior. */}
                        {!item.aprovado && (
                          <span className="absolute top-2 left-2 rounded-selo bg-black/75 border border-amber-400/50
                                           px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-300">
                            Em análise
                          </span>
                        )}
                        <span
                          className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 py-2
                                     bg-gradient-to-t from-black/85 to-transparent
                                     text-[11px] font-semibold text-paper opacity-[0]
                                     transition-opacity duration-200 group-hover/aval:opacity-100"
                        >
                          <i className="bi bi-pencil" /> Editar
                        </span>
                      </Capa>
                      <Titulo>{item.livro.titulo}</Titulo>
                      <Stars value={item.nota} size="text-xs" className="mt-0.5" />
                    </button>
                  </RevealStagger.Item>
                ) : (
                  <RevealStagger.Item key={item.id}>
                    {/* Avaliar acontece na página do livro: é lá que a
                        avaliação nasce, com as regras e o formulário. */}
                    <Link
                      to={main + "livros/" + item.id + "#opinioes"}
                      className="group/aval block w-full text-left"
                      aria-label={`Avaliar ${item.titulo}`}
                    >
                      <Capa livro={item}>
                        {item.pode_avaliar ? (
                          <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 py-2
                                           bg-gradient-to-t from-black/85 to-transparent text-[11px] font-semibold
                                           text-paper opacity-[0] transition-opacity duration-200
                                           group-hover/aval:opacity-100">
                            <i className="bi bi-star" /> Avaliar
                          </span>
                        ) : (
                          <span className="absolute top-2 left-2 rounded-selo bg-black/75 border border-white/25
                                           px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white/70">
                            <i className="bi bi-hourglass-split" /> falta ler
                          </span>
                        )}
                      </Capa>
                      <Titulo>{item.titulo}</Titulo>
                      {item.pode_avaliar ? (
                        <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-insigne-orange">
                          <i className="bi bi-star" /> Avaliar · +{COINS_POR_AVALIACAO}
                        </span>
                      ) : (
                        <span className="mt-0.5 block text-xs text-white/40">
                          Libera com {Number(item.percentual_minimo_avaliar) || 50}% lido
                          {item.progresso ? ` · você em ${Number(item.progresso.percentual) || 0}%` : ""}
                        </span>
                      )}
                    </Link>
                  </RevealStagger.Item>
                )
              )}
            </RevealStagger>

            <Pagination
              pagina={paginaAtual}
              totalPaginas={totalPaginas}
              onChange={setPagina}
              rolarAte="avaliacoes-topo"
            />
          </>
        )}

        {aba === "feitas" && avaliacoes.length > 0 && (
          <p className="text-white/40 text-xs mt-6">
            <i className="bi bi-info-circle mr-1" />
            Editar uma avaliação não gera novos Insigne Coins: o bônus é pago uma vez por
            livro.{" "}
            <Link to={main + "livros"} className="text-insigne-orange hover:underline">
              Ver o catálogo
            </Link>
          </p>
        )}
      </SectionCard>

      {/* FORA do SectionCard: o modal é `fixed inset-0`, e o `Reveal` do cartão
          põe `transform` nele durante a entrada — transform contém `fixed`. */}
      <ModalAvaliacao
        avaliacao={aberta}
        salvando={salvando}
        onSalvar={salvar}
        onFechar={() => setAbertaId(null)}
      />
    </>
  );
};

export default MinhasAvaliacoes;
